import { useAction, useAtom } from '@reatom/npm-react'
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { PairExploreItem } from '@root/features/explore/ui/pair-explore-item'
import { mergeRefs } from '@root/shared/react'
import {
  TooltipCard,
  TooltipCardParentGuardProvider,
} from '@root/shared/ui-kit/tooltip-card'
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from '@root/widgets/layout/desktop-layout/constants'
import {
  navigationExpandedAtom,
  navigationLockedAtom,
} from '@root/widgets/layout/desktop-layout/sidebar/atoms'
import { registerTopbarPauseAction } from '@root/widgets/layout/desktop-layout/topbar/atoms'

import { useForwardWheelFromPopoverWhileOpen } from './hooks/use-forward-vertical-wheel'

import type * as RadHoverCard from '@radix-ui/react-hover-card'
import type { PairModel } from '@root/entities/pair/atoms'
import type { PairExploreItemSize } from '@root/features/explore/ui/pair-explore-item'
import type { ClassNameProps } from '@root/shared/react'
import type { TooltipCardProps } from '@root/shared/ui-kit/tooltip-card'
import type { NavigationLocked } from '@root/widgets/layout/desktop-layout/sidebar/atoms'

type PairPopoverProps = {
  children: React.ReactElement<React.RefAttributes<HTMLElement>>
  pair: PairModel
  sideOffset?: number
  collisionPadding?: RadHoverCard.HoverCardContentProps['collisionPadding']
  side?: TooltipCardProps['side']
  layoutSize?: PairExploreItemSize | 'auto'
} & ClassNameProps

export const PairPopover: React.FC<PairPopoverProps> = memo(
  ({
    children,
    pair,
    layoutSize,
    sideOffset = 0,
    className,
    collisionPadding,
    side = 'bottom',
  }) => {
    const registerTopbarPause = useAction(registerTopbarPauseAction)
    const [open, setOpen] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLElement>(null)
    const nestedOverlayOpenRef = useRef(false)
    const [navExpanded] = useAtom(navigationExpandedAtom)
    const [navLocked] = useAtom(navigationLockedAtom)

    useLayoutEffect(() => {
      if (!open) {
        return
      }
      registerTopbarPause(true)
      return () => registerTopbarPause(false)
    }, [open, registerTopbarPause])

    const handleOpenChange = useCallback((next: boolean) => {
      setOpen(next)
    }, [])

    const handleItemClick = useCallback(() => {
      handleOpenChange(false)
    }, [handleOpenChange])

    useForwardWheelFromPopoverWhileOpen({
      open,
      triggerRef,
      contentRef,
      nestedOverlayOpenRef,
    })

    const trigger = React.cloneElement(children, {
      ref: mergeRefs([triggerRef, children.props.ref]),
    })

    return (
      <TooltipCardParentGuardProvider ignoreParentCloseAreaSuppression>
        <Root
          bare
          className={className}
          collisionPadding={
            collisionPadding ?? {
              left: getSidebarPopoverInset(navExpanded, navLocked),
            }
          }
          contentRef={contentRef}
          nestedOverlayOpenRef={nestedOverlayOpenRef}
          open={open}
          sideOffset={sideOffset}
          onOpenChange={handleOpenChange}
          side={side}
          clickArea={
            <Content>
              <PairExploreItem
                layoutSize={
                  layoutSize === 'auto' ? undefined : (layoutSize ?? 'large')
                }
                pair={pair}
                statsPeriod="1D"
                priceChartInterval="minutes"
                onClick={handleItemClick}
              />
            </Content>
          }
        >
          {trigger}
        </Root>
      </TooltipCardParentGuardProvider>
    )
  },
)

const getSidebarPopoverInset = (expanded: boolean, locked: NavigationLocked) =>
  (locked === 'expanded' || expanded
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH) + 10

const Content = styled.div`
  display: grid;
  min-width: 0;
`

const Root = styled(TooltipCard)`
  --tooltip-padding: 0;
  --tooltip-background: transparent;

  display: grid;
  min-width: 0;
  width: 700px;

  padding: 0;
  background: transparent;
  border: none;
`
